import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  BillSplitJoinPreviewResponseDto,
  FinalizeCollaborativeBillSplitRequestDto,
  FinalizeCollaborativeBillSplitResponseDto,
  HostCollaborativeDashboardResponseDto,
  JoinBillSplitRequestDto,
  JoinBillSplitResponseDto,
  ParticipantBillSplitContextResponseDto,
  ParticipantFinalizedResponseDto,
  SaveParticipantProposalsRequestDto,
  SaveParticipantProposalsResponseDto,
  SubmitParticipantProposalsResponseDto,
} from '@features/client/models/public-bill-split-collaboration-models';

@Injectable({
  providedIn: 'root',
})
@Injectable({ providedIn: 'root' })
export class CollaborativeBillSplitApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/public/bill-splits`;

  getJoinPreview(splitId: string): Observable<BillSplitJoinPreviewResponseDto> {
    return this.http.get<BillSplitJoinPreviewResponseDto>(
      `${this.baseUrl}/${splitId}/join-preview`,
    );
  }

  join(splitId: string, body: JoinBillSplitRequestDto): Observable<JoinBillSplitResponseDto> {
    return this.http.post<JoinBillSplitResponseDto>(`${this.baseUrl}/${splitId}/join`, body);
  }

  getParticipantContext(
    splitId: string,
    participantToken: string,
  ): Observable<ParticipantBillSplitContextResponseDto> {
    return this.http.get<ParticipantBillSplitContextResponseDto>(
      `${this.baseUrl}/${splitId}/participant-context`,
      { params: { participantToken } },
    );
  }

  getParticipantResult(
    splitId: string,
    participantToken: string,
  ): Observable<ParticipantFinalizedResponseDto> {
    return this.http.get<ParticipantFinalizedResponseDto>(
      `${this.baseUrl}/${splitId}/participant-result`,
      { params: { participantToken } },
    );
  }

  saveDraft(
    splitId: string,
    participantId: string,
    participantToken: string,
    body: SaveParticipantProposalsRequestDto,
  ): Observable<SaveParticipantProposalsResponseDto> {
    return this.http.put<SaveParticipantProposalsResponseDto>(
      `${this.baseUrl}/${splitId}/participants/${participantId}/proposals/draft`,
      body,
      { params: { participantToken } },
    );
  }

  submit(
    splitId: string,
    participantId: string,
    participantToken: string,
  ): Observable<SubmitParticipantProposalsResponseDto> {
    return this.http.post<SubmitParticipantProposalsResponseDto>(
      `${this.baseUrl}/${splitId}/participants/${participantId}/proposals/submit`,
      {},
      { params: { participantToken } },
    );
  }

  getHostDashboard(
    splitId: string,
    hostToken: string,
  ): Observable<HostCollaborativeDashboardResponseDto> {
    return this.http.get<HostCollaborativeDashboardResponseDto>(
      `${this.baseUrl}/${splitId}/host-dashboard`,
      { params: { hostToken } },
    );
  }

  finalizeSplit(
    splitId: string,
    hostToken: string,
    body: FinalizeCollaborativeBillSplitRequestDto,
  ): Observable<FinalizeCollaborativeBillSplitResponseDto> {
    return this.http.post<FinalizeCollaborativeBillSplitResponseDto>(
      `${this.baseUrl}/${splitId}/finalize`,
      body,
      { params: { hostToken } },
    );
  }
}
